import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import gsap from 'gsap';
import Button from '../Button/Button';
import styles from './ContactUs.module.scss';
import { TELEGRAM_CTA_URL } from '../../constants/cta';
import { CONTACT_CONTENT } from '../../constants/marketingContent';
import { trackGoal } from '../../analytics/metrika';
import contactBg from '../../assets/steps/bg-grad.png';

const ContactUs = () => {
  const [isImageVisible, setIsImageVisible] = useState(false);
  const imageWrapRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const imageNode = imageWrapRef.current;

    if (!imageNode) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsImageVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.35,
        rootMargin: '0px 0px -5% 0px',
      },
    );

    observer.observe(imageNode);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isImageVisible) return undefined;

    const imageWrapNode = imageWrapRef.current;
    const imageNode = imageRef.current;

    if (!imageWrapNode || !imageNode) return undefined;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return undefined;

    const isMobile = window.matchMedia('(max-width: 900px)').matches;
    const floatDistance = isMobile ? 4 : 7;
    const scaleDistance = isMobile ? 0.014 : 0.02;
    const loopDuration = isMobile ? 3.8 : 4.4;
    const loopDelay = 0.2;

    const ctx = gsap.context(() => {
      gsap.set(imageWrapNode, { y: 0 });
      gsap.set(imageNode, { scale: 1 });

      const motion = { phase: 0 };
      const setY = gsap.quickSetter(imageWrapNode, 'y', 'px');
      const setScale = gsap.quickSetter(imageNode, 'scale');

      gsap.to(motion, {
        phase: Math.PI * 2,
        duration: loopDuration,
        ease: 'none',
        repeat: -1,
        delay: loopDelay,
        onUpdate: () => {
          const y = Math.sin(motion.phase) * floatDistance;
          const scale = 1 + Math.sin(motion.phase) * scaleDistance;
          setY(y);
          setScale(scale);
        },
      });
    }, imageWrapNode);

    return () => ctx.revert();
  }, [isImageVisible]);

  const openTelegram = () => {
    trackGoal('cta_telegram_click', { source: 'contact' });
    window.open(TELEGRAM_CTA_URL, '_blank');
  };

  return (
    <section className={clsx(styles.wrap)} id="contact" style={{ backgroundImage: `url(${contactBg})` }}>
      <div className="container">
        <div className={clsx(styles.card)} id="contact-card">
          <div className={clsx(styles.content)}>
            <h2 className={clsx(styles.title, 'title')}>{CONTACT_CONTENT.title}</h2>
            <p className={clsx(styles.subtitle)}>{CONTACT_CONTENT.subtitle}</p>
            <div className={clsx(styles.steps)}>
              {CONTACT_CONTENT.steps.map((step) => (
                <div className={clsx(styles.step)} key={step}>
                  {step}
                </div>
              ))}
            </div>
            <Button
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M0 0h24v24H0z" fill="none" />
                  <g fill="none" fillRule="evenodd">
                    <path d="m12.593 23.258l-.011.002l-.071.035l-.02.004l-.014-.004l-.071-.035q-.016-.005-.024.005l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.017-.018m.265-.113l-.013.002l-.185.093l-.01.01l-.003.011l.018.43l.005.012l.008.007l.201.093q.019.005.029-.008l.004-.014l-.034-.614q-.005-.018-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.004-.011l.017-.43l-.003-.012l-.01-.01z" />
                    <path
                      fill="currentColor"
                      d="M19.777 4.43a1.5 1.5 0 0 1 2.062 1.626l-2.268 13.757c-.22 1.327-1.676 2.088-2.893 1.427c-1.018-.553-2.53-1.405-3.89-2.294c-.68-.445-2.763-1.87-2.507-2.884c.22-.867 3.72-4.125 5.72-6.062c.785-.761.427-1.2-.5-.5c-2.302 1.738-5.998 4.381-7.22 5.125c-1.078.656-1.64.768-2.312.656c-1.226-.204-2.363-.52-3.291-.905c-1.254-.52-1.193-2.244-.001-2.746z"
                    />
                  </g>
                </svg>
              }
              data-metrika-label={CONTACT_CONTENT.ctaText}
              data-metrika-section="contact"
              onClick={openTelegram}>
              {CONTACT_CONTENT.ctaText}
            </Button>
          </div>

          <div className={clsx(styles.imageWrap, isImageVisible && styles.imageWrapVisible)} ref={imageWrapRef}>
            <img ref={imageRef} src={'/img-webp/message.webp'} alt="Сообщение" className={clsx(styles.image)} loading="lazy" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;
